import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { getSize } from '../../util/getSize.js';
import { useEventListener } from '../../util/useEventListener.js';
import useUniqueId from '../../util/useUniqueId.js';
import { useFloating, offset, flip, shift, arrow, size, autoUpdate } from '@floating-ui/react-dom';
import { Box } from '../Box/Box.js';
import { Portal } from '../Portal/Portal.js';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly.js';
import { Typography } from '../Typography/Typography.js';
import { useDirectionContext, Direction } from '../../contexts/session/Locale.js';
import { ZINDEX } from '../../static/constants.js';
import { FloatingContainer } from './Container.js';
import { mapPlacement, Pointer } from './Pointer.js';

var _excluded = ["visible", "pointerPosition", "children", "content", "zIndex", "interaction", "pointerColor", "pointerPadding", "visibleClass", "useFlexWrapper", "childrenContainerProps", "containerProps", "flexWrapper", "onClick", "onBlur", "onVisibilityChange", "fixed", "autoHeightResize", "usePortal", "hasInteractiveContent", "isContentControlledAndUnannounced"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// Spacing between the tip of the arrow and the edge of the trigger element
var ARROW_OFFSET = 16; // TODO: ADSWEB-1124 Numerical tokens (spacing.l)
// Spacing between the edge of the floating container and the edge of the viewport
var EDGE_OFFSET = 12; // TODO: ADSWEB-1124 Numerical tokens (spacing.m)
var HOVER_FADE_DELAY = '0.2s';
var CLICK_FADE_DELAY = '0s';
var HIDE_TIMEOUT_MS = 350;

/**
 * INTERNAL component for a floating element.
 */
var Floater = /*#__PURE__*/React.memo(function (_ref) {
  var visibleProp = _ref.visible,
    pointerPosition = _ref.pointerPosition,
    childrenProp = _ref.children,
    content = _ref.content,
    _ref$zIndex = _ref.zIndex,
    zIndex = _ref$zIndex === void 0 ? ZINDEX.FLOATING : _ref$zIndex,
    interaction = _ref.interaction,
    pointerColor = _ref.pointerColor,
    pointerPadding = _ref.pointerPadding,
    visibleClass = _ref.visibleClass,
    useFlexWrapper = _ref.useFlexWrapper,
    childrenContainerProps = _ref.childrenContainerProps,
    containerProps = _ref.containerProps,
    flexWrapper = _ref.flexWrapper,
    onClick = _ref.onClick,
    onBlur = _ref.onBlur,
    onVisibilityChange = _ref.onVisibilityChange,
    fixed = _ref.fixed,
    _ref$autoHeightResize = _ref.autoHeightResize,
    autoHeightResize = _ref$autoHeightResize === void 0 ? true : _ref$autoHeightResize,
    _ref$usePortal = _ref.usePortal,
    usePortal = _ref$usePortal === void 0 ? false : _ref$usePortal,
    _ref$hasInteractiveCo = _ref.hasInteractiveContent,
    hasInteractiveContent = _ref$hasInteractiveCo === void 0 ? true : _ref$hasInteractiveCo,
    _ref$isContentControl = _ref.isContentControlledAndUnannounced,
    isContentControlledAndUnannounced = _ref$isContentControl === void 0 ? false : _ref$isContentControl,
    rest = _objectWithoutProperties(_ref, _excluded);
  var direction = useDirectionContext();
  var id = useUniqueId();
  var shouldMountTooltip = !hasInteractiveContent;
  var wrapperDivRef = React.useRef(null);
  var arrowRef = React.useRef(null);
  var floaterRef = React.useRef(null);
  var hideTimeoutRef = React.useRef(null);
  var _React$useState = React.useState(!!visibleProp),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    visible = _React$useState2[0],
    setVisible = _React$useState2[1];
  var _React$useState3 = React.useState(!!visibleProp),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    mount = _React$useState4[0],
    setMount = _React$useState4[1];
  var finalVisible = visibleProp !== null && visibleProp !== void 0 ? visibleProp : visible;
  var isRTL = direction === Direction.RTL;
  var pointerSize = getSize('s');
  var mappedPlacement = React.useMemo(function () {
    return mapPlacement(isRTL, pointerPosition);
  }, [isRTL, pointerPosition]);
  var _useFloating = useFloating({
      placement: mappedPlacement,
      strategy: 'fixed',
      middleware: [offset(ARROW_OFFSET)].concat(_toConsumableArray(fixed ? [] : [flip(), shift({
        padding: EDGE_OFFSET
      })]), [arrow({
        element: arrowRef
      }), size({
        padding: EDGE_OFFSET,
        apply: autoHeightResize ? function (_ref2) {
          var availableHeight = _ref2.availableHeight;
          var floater = floaterRef.current;
          if (!floater) return;
          floater.style.maxHeight = "".concat(availableHeight, "px");
        } : undefined
      })])
    }),
    x = _useFloating.x,
    y = _useFloating.y,
    refs = _useFloating.refs,
    reference = _useFloating.reference,
    floating = _useFloating.floating,
    update = _useFloating.update,
    strategy = _useFloating.strategy,
    placement = _useFloating.placement,
    _useFloating$middlewa = _useFloating.middlewareData.arrow,
    _useFloating$middlewa2 = _useFloating$middlewa === void 0 ? {} : _useFloating$middlewa,
    arrowX = _useFloating$middlewa2.x,
    arrowY = _useFloating$middlewa2.y;
  React.useLayoutEffect(function () {
    if (mount && refs.reference.current && refs.floating.current) {
      autoUpdate(refs.reference.current, refs.floating.current, update, {
        animationFrame: true,
        elementResize: true
      });
    }
  }, [update, refs.floating, refs.reference, mount]);
  var setVisibleState = React.useCallback(function (newVisible) {
    if (newVisible) setMount(true);
    setVisible(newVisible);
  }, []);
  var handleVisibilityChange = React.useCallback(function (newVisible) {
    onVisibilityChange === null || onVisibilityChange === void 0 || onVisibilityChange(newVisible);
    setVisible(newVisible);
  }, [onVisibilityChange]);

  // Remove floater after fade out
  var removeFloater = React.useCallback(function () {
    if (!finalVisible) {
      setMount(false);
    }
  }, [finalVisible]);
  var hideFloater = React.useCallback(function () {
    if (!floaterRef.current) return;
    floaterRef.current.classList.remove(visibleClass);
    hideTimeoutRef.current = setTimeout(removeFloater, HIDE_TIMEOUT_MS); // Get rid of timeout once figured out to test transitionend in interaction tests
  }, [removeFloater, visibleClass]);
  var handleFloaterCleanUp = React.useCallback(function (e) {
    var _refs$floating$curren;
    var target = e.target;
    var referenceRef = refs.reference.current;
    if (!(target instanceof HTMLElement) || !(referenceRef instanceof HTMLElement)) {
      return;
    }
    if ((_refs$floating$curren = refs.floating.current) !== null && _refs$floating$curren !== void 0 && _refs$floating$curren.contains(target) || referenceRef !== null && referenceRef !== void 0 && referenceRef.contains(target)) {
      return;
    }
    onBlur === null || onBlur === void 0 || onBlur();
    handleVisibilityChange(false);
  }, [handleVisibilityChange, onBlur, refs.floating, refs.reference]);
  var showFloater = React.useCallback(function () {
    var _floaterRef$current;
    if (hideTimeoutRef.current !== null) {
      clearTimeout(hideTimeoutRef.current);
    }
    setMount(true);
    (_floaterRef$current = floaterRef.current) === null || _floaterRef$current === void 0 || _floaterRef$current.classList.add(visibleClass);
  }, [visibleClass]);
  var handleOnClick = React.useCallback(function () {
    handleVisibilityChange(!finalVisible);
    if (!finalVisible) setMount(true);
    onClick === null || onClick === void 0 || onClick();
  }, [handleVisibilityChange, finalVisible, onClick]);
  var handleKeyDown = React.useCallback(function (event) {
    if ((event === null || event === void 0 ? void 0 : event.key) === 'Escape') {
      event.preventDefault();
      handleVisibilityChange(false);
    }
  }, [handleVisibilityChange]);
  var handleScroll = React.useCallback(function () {
    var _wrapperDivRef$curren;
    if (document.activeElement instanceof HTMLElement && (_wrapperDivRef$curren = wrapperDivRef.current) !== null && _wrapperDivRef$curren !== void 0 && _wrapperDivRef$curren.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }, []);
  var attached = finalVisible && interaction === 'click';
  var scrollAttached = finalVisible && interaction === 'hover';
  useEventListener({
    handler: handleFloaterCleanUp,
    eventName: 'click',
    attached: attached,
    capture: false
  });
  useEventListener({
    handler: handleFloaterCleanUp,
    eventName: 'focusin',
    attached: attached,
    capture: false
  });
  useEventListener({
    handler: handleScroll,
    eventName: 'touchmove',
    attached: scrollAttached,
    capture: false
  });

  // Trigger visibility class for animation
  React.useEffect(function () {
    var _floaterRef$current2;
    // TODO: ADSDEV-76 Controlled Popover with only "visible" prop doesn't animate fade-in when set to true
    (_floaterRef$current2 = floaterRef.current) === null || _floaterRef$current2 === void 0 || _floaterRef$current2.classList.toggle(visibleClass, mount && finalVisible);
  }, [mount, finalVisible, visibleClass]);
  React.useEffect(function () {
    if (finalVisible) {
      showFloater();
    } else {
      hideFloater();
    }
  }, [finalVisible, showFloater, hideFloater]);
  var triggerElement = React.useMemo(function () {
    return React.Children.map(childrenProp, function (child) {
      var attributes = isContentControlledAndUnannounced ? {
        tabIndex: 0,
        'aria-controls': id
      } : {
        tabIndex: 0,
        'aria-describedby': id
      };
      if (/*#__PURE__*/React.isValidElement(child)) {
        var _child$props$tabIndex, _child$props;
        return /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({}, attributes), {}, {
          tabIndex: (_child$props$tabIndex = child === null || child === void 0 || (_child$props = child.props) === null || _child$props === void 0 ? void 0 : _child$props.tabIndex) !== null && _child$props$tabIndex !== void 0 ? _child$props$tabIndex : 0
        }));
      }

      // If child is not a valid element, i.e. string or number, return an accessible tag '<p>'
      var variant = 'body-default';
      var attributesWithVariant = _objectSpread(_objectSpread({}, attributes), {}, {
        variant: variant
      });
      return /*#__PURE__*/React.createElement(Typography, attributesWithVariant, child);
    });
  }, [childrenProp, id, isContentControlledAndUnannounced]);
  var children = React.useMemo(function () {
    return /*#__PURE__*/React.createElement(Box, _extends({}, rest, childrenContainerProps, interaction === 'hover' ? {
      onMouseEnter: function onMouseEnter() {
        setVisibleState(true);
        onVisibilityChange === null || onVisibilityChange === void 0 || onVisibilityChange(true);
      },
      onMouseLeave: function onMouseLeave() {
        setVisibleState(false);
        onVisibilityChange === null || onVisibilityChange === void 0 || onVisibilityChange(false);
      },
      onFocus: function onFocus() {
        setVisibleState(true);
        onVisibilityChange === null || onVisibilityChange === void 0 || onVisibilityChange(true);
      },
      onBlur: function onBlur() {
        setVisibleState(false);
        onVisibilityChange === null || onVisibilityChange === void 0 || onVisibilityChange(false);
      }
    } : {
      onClick: handleOnClick
    }, {
      ref: reference,
      onKeyDown: handleKeyDown
    }), triggerElement);
  }, [rest, childrenContainerProps, interaction, handleOnClick, reference, handleKeyDown, triggerElement, setVisibleState, onVisibilityChange]);
  var floater = React.useMemo(function () {
    var prevent = function prevent(event) {
      var _event$nativeEvent, _event$nativeEvent$st;
      event === null || event === void 0 || (_event$nativeEvent = event.nativeEvent) === null || _event$nativeEvent === void 0 || (_event$nativeEvent$st = _event$nativeEvent.stopImmediatePropagation) === null || _event$nativeEvent$st === void 0 || _event$nativeEvent$st.call(_event$nativeEvent);
    };
    var top = y ? "".concat(y, "px") : 0;
    var left = x ? "".concat(x, "px") : 0;
    return /*#__PURE__*/React.createElement(Box, _extends({
      ref: floating,
      position: strategy,
      zIndex: zIndex,
      top: top,
      left: left,
      width: "max-content",
      maxWidth: "calc(100vw - ".concat(2 * EDGE_OFFSET, "px)")
    }, interaction === 'hover' && {
      onMouseEnter: function onMouseEnter() {
        setVisibleState(true);
        onVisibilityChange === null || onVisibilityChange === void 0 || onVisibilityChange(true);
      },
      onMouseLeave: function onMouseLeave() {
        setVisibleState(false);
        onVisibilityChange === null || onVisibilityChange === void 0 || onVisibilityChange(false);
      }
    }), /*#__PURE__*/React.createElement(FloatingContainer, _extends({}, containerProps, {
      display: "flex",
      flexDirection: "column",
      ref: floaterRef,
      id: id,
      onClick: prevent,
      $fadeDelay: interaction === 'click' ? CLICK_FADE_DELAY : HOVER_FADE_DELAY,
      $visibleClass: visibleClass,
      "data-testid": "floater-container"
    }), content, /*#__PURE__*/React.createElement(Pointer, {
      ref: arrowRef,
      $color: pointerColor,
      $side: placement,
      $isRTL: isRTL,
      $size: pointerSize,
      $x: arrowX,
      $y: arrowY,
      $padding: pointerPadding
    })));
  }, [y, x, floating, strategy, zIndex, interaction, containerProps, id, visibleClass, content, pointerColor, placement, isRTL, pointerSize, arrowX, arrowY, pointerPadding, setVisibleState, onVisibilityChange]);
  var wrappedContent = usePortal ? /*#__PURE__*/React.createElement(Portal, null, floater) : floater;
  var srOnlyWrappedContent = /*#__PURE__*/React.createElement(ScreenReaderOnly, {
    id: id,
    role: containerProps.role
  }, content);
  var renderContent = mount ? wrappedContent : shouldMountTooltip && srOnlyWrappedContent;
  var floaterContent = useFlexWrapper ? /*#__PURE__*/React.createElement(Box, _extends({
    ref: wrapperDivRef,
    display: "flex"
  }, flexWrapper), children, renderContent) : /*#__PURE__*/React.createElement(Box, {
    display: "inline",
    ref: wrapperDivRef
  }, children, renderContent);
  return floaterContent;
});

export { Floater };
//# sourceMappingURL=Floater.js.map
